import { ChangeEvent, useEffect, useMemo, useState } from "react";
import type { QueryDocumentSnapshot } from "firebase/firestore";
import useFirestore from "shared/hooks/useFirestore";
import useGetBranchId from "shared/hooks/useGetBranchId";
import { mapWithConcurrency } from "shared/utils/mapWithConcurrency";

interface lesson{
    name:string,
    teacher:string,
    id:string,
}

const useViewLesson = () => {
    const {getBranchId} = useGetBranchId();
    const {readFirestore, readFirestoreOneDoc} = useFirestore();
    const [lessons, setLessons] = useState<lesson[]>([]);
    const [search, setSearch] = useState("");

    useEffect(() => {
        let cancelled = false;
        const loadLessons = async () => {
            try {
                const branch_id = await getBranchId();
                if (cancelled) return;
                const response = await readFirestore(`Lesson/Branch/${branch_id}`);
                if (cancelled) return;
                if (!Array.isArray(response?.docs)) {
                    throw response instanceof Error ? response : new Error("Invalid lesson list response");
                }
                const docs: QueryDocumentSnapshot[] = response.docs;
                const teacherIds = Array.from(new Set(docs.map(doc => doc.data().teacher)))
                    .filter((uid): uid is string => typeof uid === "string" && uid !== "");

                // 같은 교사는 한 번만 읽고, 서로 다른 교사는 최대 12명 동시에 조회한다.
                const teacherNames = new Map(await mapWithConcurrency(teacherIds, async uid => {
                    if (cancelled) return [uid, ""] as [string, string];
                    try {
                        const teacher = await readFirestoreOneDoc("User/Auth/Teacher", uid);
                        return [uid, teacher.exists() ? teacher.data().name ?? "" : ""] as [string, string];
                    } catch (error) {
                        if (!cancelled) console.error("Error fetching lesson teacher:", error);
                        return [uid, ""] as [string, string];
                    }
                }, 12));

                if (cancelled) return;
                setLessons(docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        name: data.name,
                        teacher: teacherNames.get(data.teacher) ?? ""
                    };
                }));
            } catch (error) {
                if (!cancelled) {
                    console.error("Error fetching lesson list:", error);
                    setLessons([]);
                }
            }
        };
        void loadLessons();
        return () => { cancelled = true; };
        // Firestore wrappers change each render; this page loads when it mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchedLessons = useMemo(
        () => lessons.filter(item => item.name.includes(search)),
        [lessons, search]
    );

    const onChange = (e:ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    }


    return {searchedLessons, search, onChange}
}

export default useViewLesson;
